"use client";

import {
	chakra,
	Container,
	Link,
	Text,
	useMultiStyleConfig,
} from "@chakra-ui/react";
import Image from "next/image";
import NextLink from "next/link";
import { SiteLocale } from "src/__generated__/cms-schema.codegen";
import { useTranslatedString } from "src/i18n/hooks";
import { Instagram, LinkedIn, Youtube } from "src/icons";
import { SiteSettings } from "src/lib/cms/cms";
import {
	usePreview,
	useProduct,
	useSiteSettings,
	useTargetGroup,
} from "src/lib/hooks";
import { replaceStringsWithReactNodes } from "src/lib/replaceStringWithReactNode";
import { phoneNumberDisplayToTel } from "src/lib/utils";
import { CMSButton } from "../CMSButton/CMSButton";
import { Hx } from "../Heading/Heading";
import { Button } from "../ui/button";

const buildInfo = JSON.stringify(
	{
		FEX_VERSION: process.env.FEX_VERSION,
		VERCEL_URL_PREDICTED: process.env.VERCEL_URL_PREDICTED,
		VERCEL_ENV: process.env.VERCEL_ENV,
		VERCEL_GIT_COMMIT_REF: process.env.VERCEL_GIT_COMMIT_REF,
	},
	null,
	2,
);

const Footer: React.FC<{
	variant?: string;
	footerSettings: SiteSettings["footerSettings"];
}> = ({ variant, footerSettings }) => {
	const isPreview = usePreview();

	const {
		locale,
		brandConfig: { slug: brandSlug },
	} = useSiteSettings();

	const t = useTranslatedString();
	const styles = useMultiStyleConfig("Footer", { variant });

	const targetGroupCtx = useTargetGroup();
	const productCtx = useProduct();

	const getPhoneNumber = () => {
		if (productCtx?.contact) return productCtx.contact.phoneNumber;

		if (targetGroupCtx?.contact) return targetGroupCtx.contact.phoneNumber;

		return t("footer.defaultPhoneNumber");
	};

	const getEmail = () => {
		if (productCtx?.contact) return productCtx.contact.email;

		if (targetGroupCtx?.contact) return targetGroupCtx.contact.email;

		return t("footer.defaultEmail");
	};

	const phoneNumber = getPhoneNumber();

	const email = getEmail();

	const footerText = replaceStringsWithReactNodes(footerSettings.text, {
		phoneNumber: (
			<Link
				id="click_footer_phone_number"
				color="brand.blue"
				href={`tel:${phoneNumberDisplayToTel(phoneNumber)}`}
			>
				{phoneNumber}
			</Link>
		),
		email: (
			<Link
				id="click_footer_email"
				color="brand.blue"
				href={`mailto:${email}`}
			>
				{email}
			</Link>
		),
	});

	return (
		<chakra.footer __css={styles.wrapper}>
			<chakra.div __css={styles.socialWrapper}>
				<Container maxW="container.xl" sx={styles.socialInner}>
					<div className="grid grid-cols-2 gap-4 w-full mx-6 lg:mx-10">
						{locale === SiteLocale.de && <a
							href="https://www.trustedshops.de/bewertung/info_X9B5278147B078F9570F3A30CCFA37265.html"
							target="_blank"
							rel="noreferrer"
						>
							<div className="flex gap-3 items-center justify-self-start">
								<Image
									src="/img/footer/trustbadge.svg"
									alt="Trusted Shops Logo"
									width={48}
									height={48}
								/>
								<div className="flex flex-col">
									<p className="heading-5">
										{t("footer.trust.heading")}
									</p>
									<p className="text-xs font-normal">
										{t("footer.trust.paragraph")}
									</p>
								</div>
							</div>
						</a>}
						<div className="justify-self-end flex items-center">
							<Link
								href={
									brandSlug.includes("funk-experts")
										? "https://www.linkedin.com/company/funk-experts"
										: "https://de.linkedin.com/company/funk-group/"
								}
								title={t("social.linkedin.title")}
								isExternal
								id="click_linkedin"
							>
								<LinkedIn
									className="p-1.25 m-2"
									w="30px"
									h="30px"
									color="var(--color-endeavour-500)"
								/>
							</Link>
							{brandSlug.includes("funk-experts") && (
								<Link
									href="https://www.instagram.com/funkexperts"
									title={t("social.instagram.title")}
									isExternal
									id="click_instagram"
								>
									<Instagram
										className="p-1.25 m-2"
										w="30px"
										h="30px"
										color="var(--color-endeavour-500)"
									/>
								</Link>
							)}
							<Link
								href="https://www.youtube.com/channel/UCGKja91ib9Av6TWxbEHJM4A"
								title={t("social.youtube.title")}
								isExternal
								id="click_youtube"
							>
								<Youtube
									className="p-1.25 m-2"
									w="30px"
									h="30px"
									color="var(--color-endeavour-500)"
								/>
							</Link>
						</div>
					</div>
				</Container>
			</chakra.div>

			<Container maxW="container.xl">
				<chakra.div sx={styles.linksWrapper}>
					<chakra.div sx={styles.intro}>
						<Hx size="h4" as="h2" sx={styles.introTitle}>
							{footerSettings.title}
						</Hx>
						{footerSettings.text && (
							<chakra.p sx={styles.introText}>
								{footerText}
							</chakra.p>
						)}
					</chakra.div>
					<chakra.div sx={styles.linkColumns}>
						<chakra.ul sx={styles.linkList}>
							{footerSettings.leftColumn.map((link) => (
								<li key={link.id}>
									<CMSButton {...link} linkStyle="text" />
								</li>
							))}
						</chakra.ul>
						<chakra.ul sx={styles.linkList}>
							{footerSettings.rightColumn.map((link) => (
								<li key={link.id}>
									<CMSButton {...link} linkStyle="text" />
								</li>
							))}
						</chakra.ul>
					</chakra.div>
				</chakra.div>
			</Container>

			{footerSettings.cancellationPage && (
				<Container maxW="container.xl">
					<div className="flex flex-col gap-6 p-6 m-8 border rounded-lg items-center justify-between sm:flex-row md:ml-auto md:w-1/2">
						<p className="heading-5">
							{t("footer.cancel.text")}
						</p>
						<NextLink
							href={`/${footerSettings.cancellationPage.slug}`}
						>
							<Button
								variant="secondary"
								className="w-full sm:w-fit"
							>
								{t("footer.cancel.button")}
							</Button>
						</NextLink>
					</div>
				</Container>
			)}

			<chakra.div __css={styles.certificatesWrapper}>
				<Container maxW="container.xl" sx={styles.certificatesInner}>
					<chakra.div __css={styles.certificatesLogos}>
						{/* TODO: these could be fetched from the DatoCMS CDN, too */}
						<Image
							src="/img/footer/iso.jpg"
							width={70}
							height={58}
							alt="ISO-zertifiziert"
						/>
					</chakra.div>
					<chakra.div maxWidth="480px">
						<Text>{t("footer.quality")}</Text>
						<Text fontWeight="normal">{t("footer.iso")}</Text>
					</chakra.div>
				</Container>
			</chakra.div>
			<chakra.div
				__css={styles.bottomWrapper}
				data-buildinfo={isPreview ? buildInfo : "draft mode inactive"}
			/>
		</chakra.footer>
	);
};

export { Footer };

// 🔬 TBD: Please evaluate
